import { useNavigate } from 'react-router-dom'
import { Avatar, Button, Chat, Heading } from '@design-systems/canvas-ui'
import Icon, {
  ArrowUpIcon,
  ChatIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
} from '@design-systems/react-icons'
import { getUserSession } from '../../../shared/authentication/helpers/sessionStorage'
import { getBrandDetailsPath } from '../../../shared/routes/routes'

const formatBrandName = (slug = '') =>
  slug
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => `${word[0].toUpperCase()}${word.slice(1)}`)
    .join(' ')

const formatDayLabel = (value) => {
  if (!value) return ''

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const now = new Date()
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  )
  const startOfYesterday = new Date(startOfToday)
  startOfYesterday.setDate(startOfYesterday.getDate() - 1)

  if (date >= startOfToday) return 'Today'
  if (date >= startOfYesterday && date < startOfToday) return 'Yesterday'

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: 'numeric',
  }).format(date)
}

const getInitials = (name = '') => {
  const parts = `${name}`.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return 'AU'

  const initials = parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')

  return initials || 'AU'
}

const formatMarkdownText = (value = '') => {
  const escaped = String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

  return escaped
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*|__([^_]+)__/g, '<strong>$1$2</strong>')
    .replace(/\*([^*]+)\*|_([^_]+)_/g, '<em>$1$2</em>')
    .replace(/\n/g, '<br />')
}

const ChatTranscript = ({
  exchanges,
  isLoadingConversation,
  isLoadingOlder,
  olderCursor,
  onLoadOlder,
  onRate,
  onRetry,
  activeAction,
}) => {
  const navigate = useNavigate()
  const session = getUserSession()
  const currentUserName = session.fullName || session.displayName || 'User'
  const currentUserAvatarUrl =
    session.avatarUrl ||
    session.avatar ||
    session.picture ||
    session.profileImage ||
    session.imageUrl ||
    session.image_url ||
    session.profile_image ||
    ''
  const currentUserInitials = getInitials(currentUserName)
  const groupedExchanges = exchanges.reduce((groups, exchange) => {
    const bucketKey =
      exchange.created_at || exchange.answered_at || exchange.updated_at
    const groupDate = bucketKey ? new Date(bucketKey) : new Date()
    const dayKey = new Date(
      groupDate.getFullYear(),
      groupDate.getMonth(),
      groupDate.getDate(),
    ).toISOString()

    if (!groups[dayKey]) {
      groups[dayKey] = {
        id: dayKey,
        label: formatDayLabel(bucketKey),
        items: [],
      }
    }

    groups[dayKey].items.push(exchange)
    return groups
  }, {})

  const uniqueSources = (sources = []) => {
    const seen = new Set()
    return sources.filter((source) => {
      const documentId =
        source.document_version_id ||
        source.document_id ||
        source.documentId ||
        source.id ||
        source.source_id ||
        source.document?.id ||
        source.document?.document_id ||
        source.file_path ||
        source.brand ||
        source.document_type ||
        JSON.stringify(source)

      if (!documentId || seen.has(documentId)) return false
      seen.add(documentId)
      return true
    })
  }

  if (isLoadingConversation) {
    return (
      <div
        aria-label="Opening your conversation"
        className="assistant__conversation-loading"
        role="status"
      >
        <span className="assistant__loading-dot" />
        <span className="assistant__loading-dot" />
        <span className="assistant__loading-dot" />
      </div>
    )
  }

  return (
    <div className="assistant__messages">
      {olderCursor && (
        <Button
          className="assistant__load-older"
          isDisabled={isLoadingOlder}
          isLoading={isLoadingOlder}
          onClick={onLoadOlder}
          type="button"
          variant="ghost-inverted"
        >
          ↑ Load earlier messages
        </Button>
      )}
      {Object.values(groupedExchanges).map((group) => (
        <div className="assistant__day-group" key={group.id || group.label}>
          <div className="assistant__day-label">{group.label}</div>
          {group.items.map((exchange) => (
            <article className="assistant__exchange" key={exchange.exchange_id}>
              <div className="assistant__user-column">
                <Chat.Message
                  avatar={
                    <Avatar aria-label={currentUserName} size="sm">
                      {currentUserAvatarUrl ? (
                        <img
                          alt={currentUserName}
                          src={currentUserAvatarUrl}
                          style={{
                            display: 'block',
                            height: '100%',
                            objectFit: 'cover',
                            width: '100%',
                          }}
                        />
                      ) : (
                        currentUserInitials
                      )}
                    </Avatar>
                  }
                  className="assistant__chat-message"
                  variant="sender"
                >
                  {exchange.user_query}
                </Chat.Message>
                {exchange.status === 'FAILED' && (
                  <div className="assistant__error-inline">
                    <p className="assistant__error-copy">
                      This answer didn’t come through. Your question is still
                      here.
                    </p>
                    <Button
                      className="assistant__retry-button"
                      isDisabled={activeAction === exchange.exchange_id}
                      isLoading={activeAction === exchange.exchange_id}
                      onClick={() => onRetry(exchange)}
                      size="sm"
                      type="button"
                      variant="secondary"
                    >
                      Retry question
                    </Button>
                  </div>
                )}
              </div>
              {exchange.status === 'PENDING' ? (
                <Chat.Message
                  className="assistant__chat-message"
                  isLoading
                  variant="receiver"
                >
                  Finding the right guidance…
                </Chat.Message>
              ) : exchange.answer ? (
                <Chat.Message
                  className="assistant__chat-message"
                  toolbar={
                    exchange.exchange_id && (
                      <div
                        className="assistant__feedback"
                        role="group"
                        aria-label="Rate this answer"
                      >
                        <span>Helpful?</span>
                        <Button
                          aria-label="Helpful answer"
                          aria-pressed={exchange.feedback === 'UP'}
                          isDisabled={activeAction === exchange.exchange_id}
                          isIconOnly
                          onClick={() => onRate(exchange, 'UP')}
                          size="sm"
                          type="button"
                          variant={
                            exchange.feedback === 'UP'
                              ? 'secondary'
                              : 'ghost-neutral'
                          }
                        >
                          <Icon svg={ThumbsUpIcon} />
                        </Button>
                        <Button
                          aria-label="Not helpful answer"
                          aria-pressed={exchange.feedback === 'DOWN'}
                          isDisabled={activeAction === exchange.exchange_id}
                          isIconOnly
                          onClick={() => onRate(exchange, 'DOWN')}
                          size="sm"
                          type="button"
                          variant={
                            exchange.feedback === 'DOWN'
                              ? 'secondary'
                              : 'ghost-neutral'
                          }
                        >
                          <Icon svg={ThumbsDownIcon} />
                        </Button>
                      </div>
                    )
                  }
                  variant="receiver"
                >
                  {exchange.brand_used && (
                    <div className="assistant__brand-result">
                      <span>BRAND</span>
                      {exchange.brand_uuid ? (
                        <Button
                          onClick={() =>
                            navigate(getBrandDetailsPath(exchange.brand_used))
                          }
                          size="sm"
                          type="button"
                          variant="ghost"
                        >
                          {formatBrandName(exchange.brand_used)}{' '}
                          <Icon svg={ArrowUpIcon} />
                        </Button>
                      ) : (
                        <strong>{formatBrandName(exchange.brand_used)}</strong>
                      )}
                    </div>
                  )}
                  <div
                    className="assistant__answer-text"
                    dangerouslySetInnerHTML={{
                      __html: formatMarkdownText(exchange.answer || ''),
                    }}
                  />
                  {(() => {
                    const dedupedSources = uniqueSources(exchange.sources)
                    return dedupedSources.length > 0 ? (
                      <div className="assistant__sources">
                        <span>Sources</span>
                        {dedupedSources.map((source, index) => (
                          <div
                            className="assistant__source"
                            key={
                              source.document_id ||
                              source.documentId ||
                              source.id ||
                              source.chunk_id ||
                              source.file_path ||
                              `${source.document_type || 'source'}-${index}`
                            }
                          >
                            <strong>
                              {source.document_type || 'Brand resource'}
                            </strong>
                            <span>
                              {source.file_path ||
                                source.brand ||
                                'Supporting source'}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null
                  })()}
                </Chat.Message>
              ) : null}
            </article>
          ))}
        </div>
      ))}
      {!exchanges.length && (
        <div className="assistant__empty">
          <Icon aria-hidden="true" size="lg" svg={ChatIcon} />
          <Heading as="h2" size={3}>
            No messages yet
          </Heading>
          <p>Ask a question to start this conversation.</p>
        </div>
      )}
    </div>
  )
}

export default ChatTranscript
